import { useIsMobileEmailUser } from "@/app/hooks/use-is-mobile-email-user"
import type { PurchasableNft } from "@/app/nft/buy/nfts-to-buy/scope"
import { PriceInCrypto } from "@/app/price"
import { priceInCryptoAsText } from "@/app/price/utils"
import type { PriceValue } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import Link from "next/link"
import { useScanner } from "../anaconda-sdk-scanner"
import { AppToast, SuccessViewTransaction } from "./app-toast"
import { useToastError } from "./toast-util"

export const BatchBuyToast = {
  Error: function BatchBuyToastError({
    nfts,
    price,
    id,
    error,
  }: {
    nfts: PurchasableNft[]
    price: PriceValue | undefined
    id: string | number
    error: unknown
  }) {
    const { message, type } = useToastError(
      error,
      nfts[0].collection.blockchain,
    )
    const count = nfts.length

    const title = getPurchaseText({ count: nfts.length, price })
    const main = <PurchaseLink count={count} price={price} />

    if (type === "destructive") {
      return (
        <AppToast.Error
          id={id}
          title={title}
          main={main}
          second={
            <div className="font-semibold text-destructive">{message}</div>
          }
        />
      )
    } else if (type === "surface") {
      return (
        <AppToast.Warning
          id={id}
          title={title}
          main={main}
          second={
            <div className="font-semibold text-muted-foreground">{message}</div>
          }
        />
      )
    }
  },
  Pending: function BatchBuyToastPending({
    nfts,
    price,
    id,
  }: {
    nfts: PurchasableNft[]
    price: PriceValue | undefined
    id: string | number
  }) {
    const isMobileEmailUser = useIsMobileEmailUser()

    if (isMobileEmailUser) {
      return null
    }

    const count = nfts.length
    return (
      <AppToast.Pending
        id={id}
        title={getPurchaseText({ count, price })}
        main={<PurchaseLink count={count} price={price} />}
        second={
          <div className="font-semibold text-muted-foreground">
            <Trans>Pending...</Trans>
          </div>
        }
      />
    )
  },
  Success: function BatchBuyToastSuccess({
    nfts,
    price,
    id,
    transactionId,
  }: {
    nfts: PurchasableNft[]
    price: PriceValue | undefined
    id: string | number
    transactionId: string
  }) {
    const count = nfts.length
    return (
      <AppToast.Success
        id={id}
        title={getPurchaseText({ count, price })}
        main={<SuccessPurchase count={count} />}
        second={
          <SuccessViewTransaction
            transactionId={transactionId}
            blockchain={nfts[0].collection.blockchain}
          />
        }
      />
    )
  },
}

function getPurchaseText({
  count,
  price,
}: { count: number; price: PriceValue | undefined }) {
  const title = `Purchasing ${count} items for ${priceInCryptoAsText(price)}`
  return title
}

function PurchaseLink({
  count,
  price,
}: { count: number; price: PriceValue | undefined }) {
  return (
    <Trans>
      Purchasing {count} items{" "}
      {price === undefined ? null : (
        <>
          for <PriceInCrypto amount={price.amount} currency={price.currency} />
        </>
      )}
    </Trans>
  )
}

function SuccessPurchase({ count }: { count: number }) {
  return <Trans>Congrats {count} items are yours</Trans>
}
